import { cardsService, delegateService } from '@/services'
import { CardsMapper } from '@/features/Cards/cards.mapper'
import { createBalancesQueryOptions } from '@/features/Wallets/queries'
import { queryClient as globalQueryClient } from '@/shared/ui/QueryProvider/QueryProvider'
import { useCardsStore } from '@/store/cards/cards'
import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query'

export async function getUserCards() {
  const [cards, balances] = await Promise.all([
    cardsService.getUserCards(),
    globalQueryClient.fetchQuery(createBalancesQueryOptions()),
  ])

  const mainWalletAddress = balances.find((balance) => balance.isMain)?.address
  return CardsMapper.sortByMainWallet(cards, mainWalletAddress)
}

export async function getCanIssueCards() {
  const { canIssue, payload } = await cardsService.canIssue()
  return { canIssue, reason: payload ? Object.keys(payload) : [] }
}

export const cardsQueryKey = ['cards'] as const
export const canIssueCardsQueryKey = ['canIssueCards'] as const

let lastCardsDataRef: unknown = null

export const createCardsQueryOptions = (enabled: boolean = true) => queryOptions({
  queryKey: cardsQueryKey,
  enabled: enabled,
  queryFn: getUserCards,
  staleTime: 1000 * 60 * 5,
  gcTime: 0,
  select: (data) => {
    if (enabled && data !== lastCardsDataRef) {
      lastCardsDataRef = data
      useCardsStore.getState().setSelectedCard(data[0]?.id ?? null)
    }
    return data
  }
})

export const createCanIssueCardsQueryOptions = (enabled: boolean = true) => queryOptions({
  queryKey: canIssueCardsQueryKey,
  enabled: enabled,
  queryFn: getCanIssueCards,
  staleTime: 1000 * 60 * 5,
  gcTime: 0,
})

export const delegateSuggestionsQueryKey = (tokenAddress: string) => ['delegateSuggestions', tokenAddress] as const

export const createDelegateSuggestionsQueryOptions = (tokenAddress: string, enabled: boolean = true) => queryOptions({
  queryKey: delegateSuggestionsQueryKey(tokenAddress),
  queryFn: () => delegateService.getSuggestions(tokenAddress),
  enabled: enabled && !!tokenAddress,
  staleTime: 1000 * 60 * 5,
  gcTime: 1000 * 60 * 10,
})

export async function getTariffs() {
  return await cardsService.getTariffs()
}

export const tariffsQueryKey = ['tariffs'] as const

export const createTariffsQueryOptions = (enabled: boolean = true) => queryOptions({
  queryKey: tariffsQueryKey,
  enabled: enabled,
  queryFn: getTariffs,
  staleTime: 1000 * 60 * 5, // 5 minutes
  gcTime: 1000 * 60 * 10, // 10 minutes,
  select: (response) => {
    const userTariff = response.instances.find((tariff) => tariff.id === response.currentInstanceId)
    const tariffs = response.instances.sort((a, b) => a.id - b.id)
    return {
      ...response,
      tariffs,
      userTariff: userTariff,
    }
  }
})

export function useRenameCard() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ cardId, name }: { cardId: string; name: string }) => {
      return await cardsService.renameCard(cardId, name)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: cardsQueryKey })
    },
  })
}

export function useTerminateCard() {
  const queryClient = useQueryClient()
  const setSelectedCard = useCardsStore((state) => state.setSelectedCard)

  return useMutation({
    mutationFn: async ({ cardId, last4 }: { cardId: string; last4: string }) => {
      return await cardsService.blockCard(cardId, last4)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: cardsQueryKey })
      queryClient.invalidateQueries({ queryKey: canIssueCardsQueryKey })
      setSelectedCard(null)
    },
  })
}
